import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CompanyProfile, UpdateCompanyProfileInput } from '@neuroedge/shared';
import { apiClient, type ApiClientError } from './client';

/** Settings > Company profile - branding printed on quotations and other customer documents. */

const KEY = ['company-profile'] as const;

export function useCompanyProfile() {
  return useQuery<CompanyProfile, ApiClientError>({
    queryKey: KEY,
    queryFn: () => apiClient.get<CompanyProfile>('/company-profile'),
  });
}

export function useUpdateCompanyProfile() {
  const qc = useQueryClient();
  return useMutation<CompanyProfile, ApiClientError, UpdateCompanyProfileInput>({
    mutationFn: (input) => apiClient.put<CompanyProfile>('/company-profile', input),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

export function useUploadCompanyLogo() {
  const qc = useQueryClient();
  return useMutation<CompanyProfile, ApiClientError, Blob>({
    mutationFn: (image) => {
      const form = new FormData();
      form.append('file', image, image.type === 'image/png' ? 'logo.png' : 'logo.jpg');
      return apiClient.post<CompanyProfile>('/company-profile/logo', form);
    },
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

export function useRemoveCompanyLogo() {
  const qc = useQueryClient();
  return useMutation<CompanyProfile, ApiClientError, void>({
    mutationFn: () => apiClient.delete<CompanyProfile>('/company-profile/logo'),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

/** Object URL for the current logo (the route is authenticated, so it's fetched as a blob). */
export function useCompanyLogoUrl(logoAssetId: string | null | undefined): string | null {
  const blob = useQuery<Blob, ApiClientError>({
    queryKey: [...KEY, 'logo', logoAssetId],
    queryFn: async () => (await apiClient.getBlob('/company-profile/logo')).blob,
    enabled: Boolean(logoAssetId),
    staleTime: Infinity,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob.data]);
  return logoAssetId ? url : null;
}

/** Opens a sample quotation PDF with the saved branding in a new tab. */
export async function openQuotationPreview(): Promise<void> {
  // Open the tab synchronously (popup blockers), then point it at the PDF.
  const tab = window.open('', '_blank');
  try {
    const { blob } = await apiClient.getBlob('/company-profile/quotation-preview');
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}
